import { Injectable } from '@angular/core';
import {OccEndpointsService, TranslationService, UserIdService} from '@spartacus/core';
import { HttpClient } from '@angular/common/http';
import {catchError, map, Observable, switchMap} from 'rxjs';

import { failStatus, successStatus } from '../../../../core/models/request-status.model';
import {
    Invoice, InvoiceDetails,
    InvoiceRequestConfig,
    InvoicesRequestConfig,
    InvoicesStatusRequest,
    InvoiceStatusRequest, Line,
} from "./invoice.model";

@Injectable({
  providedIn: 'root',
})
export class InvoiceAdapter {
  constructor(
    private http: HttpClient,
    private translationService: TranslationService,
    private occEndPointService: OccEndpointsService,
    private userIdService: UserIdService
  ) {}

  getInvoices(invoicesRequestConfig: InvoicesRequestConfig): Observable<InvoicesStatusRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('getInvoices', {
          urlParams: {
            userId: userId,
          },
          queryParams:{
            fromDate: invoicesRequestConfig.fromDate,
            toDate: invoicesRequestConfig.toDate,
            openOnly: invoicesRequestConfig.openOnly,
            poNumber: invoicesRequestConfig.poNumber,
            invoiceNumber: invoicesRequestConfig.invoiceNumber,
            orderNumber: invoicesRequestConfig.orderNumber,
            catalogNumber: invoicesRequestConfig.catalogNumber,
            partNumber: invoicesRequestConfig.partNumber,
            customerPartNumber: invoicesRequestConfig.customerPartNumber,
          }
        });

        return this.http.get(url).pipe(
          map((response: any) => ({
            invoices: this.normalizeInvoices(response),
            ...successStatus,
          })),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
              })),
            );
          }),
        );
      }),
    );
  }
  getInvoice(invoiceRequestConfig: InvoiceRequestConfig): Observable<InvoiceStatusRequest> {
    return this.userIdService.getUserId().pipe(
        switchMap(userId => {
          const url = this.occEndPointService.buildUrl('getInvoice', {
            urlParams: {
              userId: userId,
              uid: invoiceRequestConfig.uid,
            }
          });

          return this.http.get(url).pipe(
              map((response: any) => ({
                invoice: this.normalizeInvoice(response),
                ...successStatus,
              })),
              catchError(() => {
                return this.translationService.translate('generic.error').pipe(
                    map(textError => ({
                      message: textError,
                      ...failStatus,
                    })),
                );
              }),
          );
        }),
    );
  }

  getInvoicesExport(invoicesRequestConfig: InvoicesRequestConfig): Observable<any> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('exportInvoices', {
          urlParams: {
            userId: userId,
          },
          queryParams:{
            fromDate: invoicesRequestConfig.fromDate,
            toDate: invoicesRequestConfig.toDate,
            openOnly: invoicesRequestConfig.openOnly,
            poNumber: invoicesRequestConfig.poNumber,
            invoiceNumber: invoicesRequestConfig.invoiceNumber,
            orderNumber: invoicesRequestConfig.orderNumber,
            catalogNumber: invoicesRequestConfig.catalogNumber,
            partNumber: invoicesRequestConfig.partNumber,
            customerPartNumber: invoicesRequestConfig.customerPartNumber,
          }
        });

        return this.http.get(url, { responseType: 'json' });
      })
    );
  }

  getInvoiceExport(invoiceRequestConfig: InvoiceRequestConfig): Observable<any> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('exportInvoice', {
          urlParams: {
            userId: userId,
            uid: invoiceRequestConfig.uid,
            fileType: invoiceRequestConfig.fileType
          },
        });

        return this.http.get(url, { responseType: 'json' });
      })
    );
  }

  normalizeInvoice(_invoice: any): InvoiceDetails {
    const billAddress = `${_invoice?.billToAddress1} ${_invoice?.billToAddress2}, <br> ${_invoice?.billToContact},
                                ${_invoice?.billToPostalCode}, <br> ${_invoice?.billToCountry}`;

    const shipAddress = `${_invoice?.shipToAddress1} ${_invoice?.shipToAddress2}, <br> ${_invoice?.shipToPostalCode},
                                <br> ${_invoice?.shipToCountry}`;

    const lines: Line[] = [];

    (_invoice.lines || []).forEach((line: any) => {
        lines.push(
            {
                lineNumber: line?.lineNumber || 0,
                poLineNumber: line?.poLineNumber || 0,
                partNumber: line?.partNumber || '',
                catalogNumber: line?.catalogNumber || '',
                customerPartNumber: line?.customerPartNumber || '',
                quantityOrdered: line?.quantityOrdered || 0,
                quantityShipped: line?.quantityShipped || 0,
                sellingPrice: {
                  value: line?.sellingPrice?.value || 0,
                  formattedValue: line?.sellingPrice?.formattedValue || '',
                },
                total: {
                  value: line?.total?.value || 0,
                  formattedValue: line?.total?.formattedValue || '',
                },
                uom: line?.uom || '',
                tariff: line?.tariff || ''
            }
        );
    });

    return {
      invoiceNumber: _invoice?.invoiceNumber || '',
      accountName: _invoice?.accountName || '',
      invoiceDate: _invoice?.invoiceDate || '',
      invoiceTotal: {
        value: _invoice?.totalLines?.value || 0,
        formattedValue: _invoice?.totalLines?.formattedValue || '',
      },
      billToAddress: billAddress,
      shipToAddress: shipAddress,
      remitToAddress: _invoice?.remitToAddress || '',
      invoicePriceSummary: {
        totalLines: {
          value: _invoice?.totalLines?.value || 0,
          formattedValue: _invoice?.totalLines?.formattedValue || '',
        },
        shipping: {
          value: _invoice?.totalShipping?.value || 0,
          formattedValue: _invoice?.totalShipping?.formattedValue || '',
        },
        tax: {
          value: _invoice?.totalTax?.value || 0,
          formattedValue: _invoice?.totalTax?.formattedValue || '',
        },
        total: {
          value: _invoice?.totalLines?.value || 0,
          formattedValue: _invoice?.totalLines?.formattedValue || '',
        },
        dueDate: _invoice?.dueDate || ''
      },
      orderUid: _invoice?.orderUid || '',
      orderId: _invoice?.orderId || 0,
      orderNumber: _invoice?.orderNumber || 0,
      customerPONumber: _invoice?.customerPONumber || '',
      paymentTerm: _invoice?.paymentTerm || '',
      currencyCode: _invoice?.currencyCode || '',
      salesRepName: _invoice?.salesRepName || '',
      shipDate: _invoice?.shipDate || '',
      carrier: _invoice?.shippingCarrier || '',
      trackingInfo: _invoice?.trackingInfo || '',
      lines: lines
    }
  };

  normalizeInvoices(_invoices: any): Invoice[] {
    const invoices: Invoice[] = [];

    (_invoices || []).forEach((invoice: any) => {
      invoices.push({
        invoiceNumber: invoice?.invoiceNumber || 0,
        invoiceDate: invoice?.invoiceDate || '',
        grossAmount: {
          value: invoice?.totalRemaining.value || 0,
          formattedValue: invoice?.totalRemaining.formattedValue || '',
        },
        amountOwed: {
          value: invoice?.totalOriginal.value || 0,
          formattedValue: invoice?.totalOriginal.formattedValue || '',
        },
        poNumber: invoice?.poNumber || '',
        dueDate: invoice?.dueDate || '',
        paymentTerm: invoice?.paymentTerm || '',
        status: invoice?.status || '',
        creditAdj: {
          value: invoice?.totalApplied.value || 0,
          formattedValue: invoice?.totalApplied.formattedValue || '',
        },
        orderNumber: invoice?.orderNumber || 0,
        invoiceUid: invoice?.invoiceUid || '',
      });
    });

    return invoices;
  }

}
